<script lang="ts">
    import { navigate, onNavigate } from '@otto-ec/global-resources/navigation';
    import { otto } from '@otto-ec/global-resources/nexus';
    import { untrack } from 'svelte';
    import { navigationLog as log } from '@shared/log.browser.ts';
    import type {
        SelectionPage,
        SelectionState,
        SelectionUpdate,
    } from '@tilelist/application/ports/SelectionEvents.ts';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import { appendHeurekaFragment } from './Utils.browser.ts';

    /**
 *
 *
 *
 *
 *
 *
 */

    type Checkpoint = SelectionState;

    const wiring = injectWiring(['selectionEvents']);

    function isSelectionPage(value: unknown): value is SelectionPage {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
        return (
            Object.keys(value).every((key) => ['sortOrder', 'layout', 'offset', 'comparisonId'].includes(key)) &&
            (!('sortOrder' in value) || typeof value.sortOrder === 'string') &&
            (!('layout' in value) || typeof value.layout === 'string') &&
            (!('offset' in value) || typeof value.offset === 'string') &&
            (!('comparisonId' in value) || typeof value.comparisonId === 'string')
        );
    }

    function isCheckpoint(value: unknown): value is Checkpoint {
        if (typeof value !== 'object' || value === null) return false;
        return (
            'rule' in value &&
            typeof value.rule === 'string' &&
            'page' in value &&
            isSelectionPage(value.page) &&
            'url' in value &&
            typeof value.url === 'string' &&
            'entryRule' in value &&
            typeof value.entryRule === 'string'
        );
    }

    function urlFor(rawUrl: string): string {
        return wiring.onex.isToggleActive('DA_KEEP_HEUREKA_SHEET_FRAGMENT') ? appendHeurekaFragment(rawUrl) : rawUrl;
    }

    async function applyCheckpoint(url: string, history: 'push' | 'replace', state: Checkpoint): Promise<void> {
        try {
            await navigate(url, {
                history,
                state,
            });
        } catch {
            /*                                                        */
            log.error('Failed to apply checkpoint', { url, history, state });
        }
    }

    function handleResolved(update: SelectionUpdate): void {
        const url = urlFor(update.selection.url);
        wiring.trackingService?.trackUrl(url);
        if (!update.historic) {
            void applyCheckpoint(url, 'push', { ...update.selection, url });
        }
    }

    function relativeLocation(url: URL): string {
        return url.pathname + url.search;
    }

    function handleNavigation(event: Parameters<Parameters<typeof onNavigate.subscribe>[0]>[0]): void {
        if (event.type !== 'popstate') return;
        /*                                                                              */
        /*                                                                                  */
        if (relativeLocation(event.from.url) === relativeLocation(event.destination.url)) return;
        const restored = event.destination.state;
        if (isCheckpoint(restored)) {
            void wiring.selectionEvents.assign(restored, {
                operationId: `history-${Date.now()}`,
                context: { historic: true, shouldScroll: true },
            });
        }
    }

    function subscribe() {
        void (async () => {
            await otto.selection;
            const initialState: Checkpoint = otto.selection.current.get();
            if (!isCheckpoint(initialState)) {
                log.error('Initial state is not a valid checkpoint', { initialState });
                return;
            }
            await applyCheckpoint(location.href, 'replace', initialState);
        })();

        const unsubscribe = wiring.selectionEvents.onUpdate(handleResolved);
        const unsubscribeRestore = onNavigate.subscribe(handleNavigation);
        return () => {
            unsubscribe();
            unsubscribeRestore();
        };
    }

    $effect(() => {
        const selectionEvents = wiring.selectionEvents;
        if (!selectionEvents) return;
        return untrack(subscribe);
    });
</script>
